import { useCallback } from 'react';
import { apiService } from '@/services/api';
import { Autocomplete, AutocompleteOption } from './Autocomplete';
import { useTranslation } from 'react-i18next';

interface Neighborhood {
  id: string;
  name: string;
  city: {
    id: string;
    name: string;
    state: {
      id: string;
      code: string;
      name: string;
    };
  };
}

interface NeighborhoodAutocompleteProps {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  disabled?: boolean;
}

export function NeighborhoodAutocomplete({
  value,
  onChange,
  className,
  disabled,
}: NeighborhoodAutocompleteProps) {
  const { t } = useTranslation();

  const searchNeighborhoods = useCallback(async (query: string): Promise<AutocompleteOption[]> => {
    try {
      const neighborhoods = await apiService.get<Neighborhood[]>(
        `/location/neighborhoods?search=${encodeURIComponent(query)}&limit=5`
      );

      if (!Array.isArray(neighborhoods)) {
        return [];
      }

      return neighborhoods.map(neighborhood => ({
        value: neighborhood.name,
        label: neighborhood.name,
        subtitle: `${neighborhood.city.name} - ${neighborhood.city.state.code}`,
      }));
    } catch (error) {
      console.error('Failed to search neighborhoods:', error);
      return [];
    }
  }, []);

  return (
    <Autocomplete
      value={value}
      onChange={onChange}
      onSearch={searchNeighborhoods}
      placeholder={t('leads.autoTransfer.searchNeighborhoods')}
      label={t('leads.autoTransfer.neighborhoods')}
      disabled={disabled}
      className={className}
      minChars={2}
    />
  );
}
